/**
 * Rule 37(1) ITC Reversal dashboard.
 *
 * Section 16(2) proviso + Rule 37(1) CGST: if vendor unpaid within 180 days
 * from the invoice date, ITC availed must be reversed (added to output GST +
 * 18% interest u/s 50). We surface a 150-day heads-up so payment can be
 * chased BEFORE the reversal is forced.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, Play } from "lucide-react";

const fmtINR = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FLAG = {
  green:    ["text-emerald-600 border-emerald-500/40 bg-emerald-500/5", "OK"],
  warning:  ["text-amber-600 border-amber-500/40 bg-amber-500/5", "150-180d · CHASE"],
  overdue:  ["text-red-500 border-red-500/40 bg-red-500/5", ">180d · MUST REVERSE"],
  reversed: ["text-neutral-500 border-neutral-500/40 bg-neutral-500/5", "REVERSED"],
  paid:     ["text-blue-600 border-blue-500/40 bg-blue-500/5", "PAID IN TIME"],
};

export default function Rule37Reversal() {
  const today = new Date().toISOString().slice(0,10);
  const [asOf, setAsOf] = useState(today);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/rule37", { params: { as_of: asOf } })
      .then(r => setD(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  }, [asOf]);
  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    if (!window.confirm("Run the Rule 37 reversal sweep now?\n\nThis will auto-post a reversal JV for every bill unpaid > 180 days. Idempotent — safe to run any time.")) return;
    setSweepBusy(true);
    try {
      const r = await api.post("/reports/rule37/run-sweep");
      toast.success(`Sweep complete · reversed ${r.data.reversed_bill_count} bill(s) · ${fmtINR(r.data.total_itc_reversed)} ITC`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    }
    setSweepBusy(false);
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="rule37-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Rule 37(1) · CGST</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">ITC Reversal · 180-day Rule</h1>
          <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Bills unpaid for &gt; 180 days force an ITC reversal. Amber flag at
            day 150 gives you a 30-day window to chase the payment. Green means
            all clear; Red means the reversal JV is due; Grey means already reversed.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">As of</Label>
          <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
            className="h-8 w-40 text-sm" data-testid="rule37-as-of" />
          <Button onClick={runSweep} disabled={sweepBusy}
            className="rounded-sm bg-red-500 hover:bg-red-500/90 text-white ml-2 gap-1.5" data-testid="rule37-run-sweep">
            {sweepBusy ? <RotateCcw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {sweepBusy ? "Running…" : "Run reversal sweep"}
          </Button>
        </div>
      </div>

      {/* Summary */}
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="rule37-summary">
          {["green","warning","overdue","reversed","paid"].map(k => {
            const [cls, label] = FLAG[k];
            return (
              <div key={k} className={`card-sharp p-4 border-l-4 ${cls}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest">{label}</div>
                <div className="text-2xl font-bold mt-1 font-mono">{d.stats[k] || 0}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">bill(s)</div>
              </div>
            );
          })}
        </div>
      )}

      {d?.totals?.itc_at_risk > 0 && (
        <div className="card-sharp p-3 border-l-4 border-l-red-500 bg-red-500/5 flex items-start gap-2" data-testid="rule37-at-risk">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <b>{fmtINR(d.totals.itc_at_risk)} of ITC is at risk of reversal.</b> Contact
            these vendors NOW — every reversed ITC is added to your output GST liability
            with 18% p.a. interest under Section 50. Once paid, you can re-claim it
            by posting a re-claim JV.
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-sharp overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-2.5">Bill</th>
              <th className="text-left p-2.5">Vendor</th>
              <th className="text-right p-2.5">Grand Total</th>
              <th className="text-right p-2.5">Balance</th>
              <th className="text-right p-2.5">ITC Amt</th>
              <th className="text-center p-2.5">Days Since Bill</th>
              <th className="text-center p-2.5">Days to Reversal</th>
              <th className="text-center p-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={8} className="text-center text-muted-foreground p-6">Loading…</td></tr>}
            {!loading && (d?.rows || []).length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted-foreground p-6 italic">No purchase bills with ITC · nothing to track</td></tr>
            )}
            {(d?.rows || []).map((r, i) => {
              const [cls, label] = FLAG[r.flag];
              return (
                <tr key={r.bill_id} className="hover:bg-muted/30" data-testid={`rule37-row-${i}`}>
                  <td className="p-2.5">
                    <div className="font-mono font-semibold">{r.bill_number}</div>
                    <div className="text-[10px] text-muted-foreground">{r.bill_date}{r.vendor_bill_number ? ` · vendor: ${r.vendor_bill_number}` : ""}</div>
                  </td>
                  <td className="p-2.5">
                    <div className="font-medium">{r.vendor_name}</div>
                    <div className="text-[9px] text-muted-foreground">
                      {r.vendor_gstin || "no GSTIN"}
                      {r.vendor_msme_status && r.vendor_msme_status !== "not_registered" && (
                        <span className="ml-1 px-1 py-0 bg-orange-100 text-orange-700 rounded-sm">MSME</span>
                      )}
                    </div>
                  </td>
                  <td className="p-2.5 text-right font-mono">{fmtINR(r.grand_total)}</td>
                  <td className="p-2.5 text-right font-mono font-semibold">{fmtINR(r.balance_due)}</td>
                  <td className="p-2.5 text-right font-mono">{fmtINR(r.itc_amount)}</td>
                  <td className="p-2.5 text-center font-mono">{r.days_since_invoice}</td>
                  <td className="p-2.5 text-center font-mono">
                    {r.itc_reversed
                      ? <span className="text-muted-foreground">—</span>
                      : r.days_to_reversal === 0
                          ? <span className="text-red-500 font-bold">DUE</span>
                          : <span>{r.days_to_reversal}d</span>}
                  </td>
                  <td className="p-2.5 text-center">
                    <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${cls}`}>{label}</span>
                    {r.payment_realised_at && <div className="text-[9px] text-muted-foreground mt-0.5">paid {r.payment_realised_at.slice(0,10)}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
